import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageStyleBaseComponent } from '../../../shared/components/base/page-style-base/page-style-base.component';
import { PaymentActionService } from '../../../shared/services/payment-action.service';
import { PaymentOption } from '../../../shared/models/payment-option.model';
import { PageStyleService } from '../../../shared/services/page-style.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface PaymentState {
  showErrorCard: boolean;
  isLoading: boolean;
  isRedirecting: boolean;
}

@Component({
  selector: 'app-payment-selection',
  templateUrl: './payment-selection.component.html',
  styleUrls: ['./payment-selection.component.scss']
})
export class PaymentSelectionComponent extends PageStyleBaseComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly MAX_AMOUNT = 10000;
  readonly MIN_AMOUNT = 0;

  // Route parameters
  slug: string;
  companySlug: string;
  amountParamFromURL: number | null = null;

  // Payment options
  paymentOptions: number[] = [];
  allowCustomAmount = false;
  paymentGuid: string | undefined;
  paymentOptionResult: PaymentOption | null = null;

  // Selection state
  selectedAmountOption: number | 'custom' | null = null;
  customAmountInput = '';
  isCustomSelected = false;

  // UI state
  state: PaymentState = {
    showErrorCard: false,
    isLoading: false,
    isRedirecting: false
  };
  // Styling
  public userCss = '';

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected paymentActionService: PaymentActionService,
    protected pageStyleService: PageStyleService,
    private elementRef: ElementRef,
  ) {
    super(pageStyleService);
  }

  ngOnInit(): void {
    this.initializeRouteSubscriptions();
  }

  protected setPaymentActionPageCss(): void {
    const element = this.elementRef.nativeElement;
    const ngContentAttribute: any = Array.from(element.attributes)[0];
    const ngContentName = ngContentAttribute.name;

    this.userCss = `${this.pageStyle.PaymentActionPageCss}`; // TODO: add validation

    const regex = /(\..*\{)/g;
    const match = this.userCss.match(regex);
    console.log(match);
    this.userCss = this.userCss.replace(regex, `[${ngContentName}] ` + '$1');

    const styleElement = document.createElement('style');
    styleElement.textContent = this.userCss;
    document.head.appendChild(styleElement);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeRouteSubscriptions(): void {
    // Handle query parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(queryParams => {
        const amountParam = queryParams.amount;
        if (amountParam) {
          this.amountParamFromURL = this.parseAmount(amountParam);
        }
      });

    // Handle route parameters
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.companySlug = params.companySlug;
        this.slug = params.slug;
        this.fetchPaymentOptions();
      });
  }

  private parseAmount(amount: string): number | null {
    const normalizedAmount = amount.replace(',', '.');
    const parsedAmount = parseFloat(normalizedAmount);
    return !isNaN(parsedAmount) ? parsedAmount : null;
  }

  private fetchPaymentOptions(): void {
    this.state.isLoading = true;
    this.state.showErrorCard = false;

    this.paymentActionService.getPaymentOptions(
      this.companySlug,
      this.slug,
      (options: PaymentOption) => {
        if (!options) {
          this.handlePaymentOptionError();
          return;
        }

        this.initializePaymentOptions(options);
        this.handleInitialAmountSelection();
        this.initializeViaPaymentActionId(this.paymentGuid, () => {
          this.setPaymentActionPageCss();
        });
        this.state.isLoading = false;
      }
    );
  }

  private initializePaymentOptions(options: PaymentOption): void {
    this.paymentOptionResult = options;
    this.paymentGuid = options.Id;
    this.paymentOptions = options.PredefinedAmountsArray || [];
    this.allowCustomAmount = options.AllowCustomAmount || false;
  }

  private handlePaymentOptionError(): void {
    this.state.showErrorCard = true;
    this.state.isLoading = false;
    this.paymentOptionResult = null;
  }

  private handleInitialAmountSelection(): void {
    // Case 1: Single predefined amount with no custom amount allowed
    if (this.paymentOptions.length === 1 && !this.allowCustomAmount) {
      this.selectedAmountOption = this.paymentOptions[0];
      this.navigateToPayment(this.paymentOptions[0]);
      return;
    }

    // Case 2: Amount from URL matches predefined amount
    if (this.amountParamFromURL !== null && this.isValidPredefinedAmount(this.amountParamFromURL)) {
      this.selectedAmountOption = this.amountParamFromURL;
      this.navigateToPayment(this.amountParamFromURL);
      return;
    }

    // Case 3: Custom amount from URL
    if (this.amountParamFromURL !== null && this.isValidCustomAmount(this.amountParamFromURL)) {
      this.selectedAmountOption = 'custom';
      this.customAmountInput = this.amountParamFromURL.toString();
      this.isCustomSelected = true;
    }
  }

  private isValidPredefinedAmount(amount: number | null): boolean {
    return amount !== null && this.paymentOptions.includes(amount);
  }

  private isValidCustomAmount(amount: number | null): boolean {
    return amount !== null &&
      amount > this.MIN_AMOUNT &&
      amount <= this.MAX_AMOUNT &&
      this.allowCustomAmount;
  }

  onAmountOptionChange(option: number | 'custom'): void {
    this.isCustomSelected = option === 'custom';
    if (!this.isCustomSelected) {
      this.customAmountInput = '';
    }
  }

  onCustomAmountInput(event: Event): void {
    let value = (event.target as HTMLInputElement).value;
    value = value.replace(/[^\d.,]/g, '');

    const commaCount = (value.match(/,/g) || []).length;
    const dotCount = (value.match(/\./g) || []).length;

    if (commaCount > 1 || dotCount > 1 || (commaCount + dotCount) > 1) {
      value = value.replace(/[.,]/g, (match, offset, text) => {
        return offset === text.lastIndexOf(match) ? match : '';
      });
    }

    this.customAmountInput = value;
  }

  isValidPaymentSelection(): boolean {
    if (this.selectedAmountOption === 'custom') {
      const customAmount = this.parseAmount(this.customAmountInput);
      return customAmount !== null && this.validateAmount(customAmount);
    }

    return this.validateAmount(this.selectedAmountOption as number);
  }

  formatAmount(amount: number): string {
    return amount.toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  private validateAmount(amount: number): boolean {
    return amount > this.MIN_AMOUNT && amount <= this.MAX_AMOUNT;
  }

  private navigateToPayment(amount: number, isInstantRedirect: boolean = false): void {
    if (!this.paymentGuid || !this.validateAmount(amount)) {
      return;
    }

    this.state.isRedirecting = true;
    this.paymentActionService.createPaymentUrl(
      this.paymentGuid,
      amount,
      (paymentUrl: string) => {
        try {
          if (!paymentUrl) {
            this.state.isRedirecting = false;
            return;
          }
          if (isInstantRedirect) {
            window.location.replace(paymentUrl);
          } else {
            window.location.href = paymentUrl;
          }
        } catch (error) {
          console.error('Navigation error:', error);
          this.state.showErrorCard = true;
          this.state.isRedirecting = false;
        }
      }
    );
  }

  onPaymentSubmit(): void {
    if (!this.isValidPaymentSelection() || !this.paymentGuid) {
      return;
    }

    let finalAmount: number;

    if (this.selectedAmountOption === 'custom') {
      const customAmount = this.parseAmount(this.customAmountInput);
      if (customAmount === null) {
        return;
      }
      finalAmount = customAmount;
    } else {
      finalAmount = this.selectedAmountOption as number;
    }

    this.navigateToPayment(finalAmount);
  }
}
